import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Search,
  MessageCircle,
  Loader2,
  Users,
  Settings,
  LogOut,
  User,
  StickyNote,
  PanelLeftClose,
  PanelLeft,
  UserCheck,
  Inbox,
} from "lucide-react";
import AgentChatView from "@/components/agent/AgentChatView";
import ConversationItem from "@/components/agent/ConversationItem";
import { Link } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAgentServices } from "@/hooks/useAgentServices";

interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  city: string | null;
  emirate: string | null;
  country: string | null;
  avatar_url: string | null;
}

interface ServiceInfo {
  id: string;
  name: string;
}

interface Conversation {
  id: string;
  user_id: string;
  assigned_agent_id: string | null;
  status: "open" | "pending" | "closed";
  subject: string | null;
  service: string | null;
  service_id: string | null;
  last_message_at: string;
  last_message_preview: string | null;
  unread_count_agent: number;
  created_at: string;
  user_profile?: UserProfile;
  is_pinned_by_agent?: boolean;
  pinned_at?: string | null;
  is_archived?: boolean;
  archived_at?: string | null;
  service_info?: ServiceInfo | null;
}

interface ServiceOption {
  id: string;
  name: string;
}

// Sort priority: open (0) -> pending (1) -> closed (2)
const getStatusPriority = (status: string): number => {
  switch (status) {
    case "open": return 0;
    case "pending": return 1;
    case "closed": return 2;
    default: return 3;
  }
};

import { setViewingConversation } from "@/hooks/useRealtimeNotifications";

export default function AgentInboxPage() {
  const { user, isAdmin, isAgent, signOut, profile } = useAuth();
  const isMobile = useIsMobile();
  
  // Fetch agent's assigned services
  const { data: agentServices = [] } = useAgentServices(user?.id);
  const agentServiceIds = agentServices.map(s => s.id);
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedConversation, setSelectedConversationInternal] = useState<Conversation | null>(null);
  
  // Wrap setter to also update realtime notification tracking
  const setSelectedConversation: React.Dispatch<React.SetStateAction<Conversation | null>> = useCallback((value) => {
    setSelectedConversationInternal((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      setViewingConversation(next?.id || null);
      return next;
    });
  }, []);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [serviceFilter, setServiceFilter] = useState<string>("all");
  const [assignmentFilter, setAssignmentFilter] = useState<"all" | "mine" | "unassigned">("all");
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  
  // Notes sheet state
  const [notesSheetOpen, setNotesSheetOpen] = useState(false);
  const [notesConversation, setNotesConversation] = useState<Conversation | null>(null);
  const [notes, setNotes] = useState<{ id: string; content: string; created_at: string; agent_id: string }[]>([]);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  
  const PAGE_SIZE = 50;
  
  // Sidebar collapsed state (desktop only)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Fetch services for filtering
  useEffect(() => {
    const fetchServices = async () => {
      const { data } = await supabase
        .from("services")
        .select("id, name")
        .eq("active", true)
        .order("sort_order", { ascending: true });
      
      if (data) {
        setServiceOptions(data);
      }
    };
    fetchServices();
  }, []);

  // Fetch conversations with profiles separately
  const fetchConversations = useCallback(async (offset = 0, replace = true) => {
    if (replace) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    const { data: convData, error: convError } = await supabase
      .from("conversations")
      .select("*, service_info:services(id, name)")
      .order("last_message_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    if (convError) {
      console.error("Error fetching conversations:", convError);
      toast.error("Failed to load conversations");
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    if (!convData || convData.length === 0) {
      if (replace) {
        setConversations([]);
      }
      setHasMore(false);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const userIds = [...new Set(convData.map(c => c.user_id))];
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("id, first_name, last_name, email, phone, address_line1, city, emirate, country, avatar_url")
      .in("id", userIds);

    const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

    const formatted = convData.map((conv: any) => ({
      ...conv,
      user_profile: profilesMap.get(conv.user_id) || null,
    }));
    
    if (replace) {
      setConversations(formatted);
    } else {
      setConversations(prev => [...prev, ...formatted]);
    }
    
    setHasMore(convData.length === PAGE_SIZE);
    setLoading(false);
    setLoadingMore(false);
  }, [PAGE_SIZE]);

  // Initial fetch
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const loadMoreConversations = () => {
    if (!loadingMore && hasMore) {
      fetchConversations(conversations.length, false);
    }
  };

  // Real-time subscription for conversations
  useEffect(() => {
    const channel = supabase
      .channel("agent-conversations")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversations" },
        async (payload) => {
          if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
            const updated = payload.new as Conversation;
            
            const { data: profile } = await supabase
              .from("profiles")
              .select("first_name, last_name, email, phone, address_line1, city, emirate, country, avatar_url")
              .eq("id", updated.user_id)
              .maybeSingle();

            setConversations((prev) => {
              const exists = prev.find(c => c.id === updated.id);
              if (exists) {
                return prev.map((c) =>
                  c.id === updated.id
                    ? { ...updated, user_profile: profile || c.user_profile }
                    : c
                );
              } else {
                return [...prev, { ...updated, user_profile: profile || undefined }];
              }
            });

            if (selectedConversation?.id === updated.id) {
              setSelectedConversation(prev => 
                prev ? { ...updated, user_profile: profile || prev.user_profile } : null
              );
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConversation?.id]);

  const handleTakeOver = async (convId: string) => {
    const { data, error } = await supabase.rpc('take_over_conversation', { p_conversation_id: convId });
    if (error) { toast.error("Échec de la prise en charge"); return; }
    const result = data as { success: boolean; error?: string };
    if (!result.success) {
      toast.error(result.error === 'ALREADY_ASSIGNED' ? "Déjà assignée" : result.error || "Échec");
      return;
    }
    toast.success("Conversation assignée");
  };

  const handleClose = async (convId: string) => {
    if (!isAdmin) { toast.error("Seuls les admins peuvent fermer"); return; }
    const { data, error } = await supabase.rpc('close_conversation', { p_conversation_id: convId });
    if (error || !(data as any)?.success) { toast.error("Échec de la fermeture"); return; }
    toast.success("Conversation fermée");
  };

  const handleReopen = async (convId: string) => {
    if (!isAdmin) { toast.error("Seuls les admins peuvent réouvrir"); return; }
    const { data, error } = await supabase.rpc('reopen_conversation', { p_conversation_id: convId });
    if (error || !(data as any)?.success) { toast.error("Échec de la réouverture"); return; }
    toast.success("Conversation réouverte");
  };

  const handleTogglePin = async (convId: string, isPinned: boolean) => {
    const { error } = await supabase
      .from("conversations")
      .update({ is_pinned_by_agent: !isPinned, pinned_at: !isPinned ? new Date().toISOString() : null })
      .eq("id", convId);

    if (error) { toast.error("Erreur lors de l'épinglage"); return; }

    setConversations((prev) =>
      prev.map((c) =>
        c.id === convId
          ? { ...c, is_pinned_by_agent: !isPinned, pinned_at: !isPinned ? new Date().toISOString() : null }
          : c
      )
    );
    
    if (selectedConversation?.id === convId) {
      setSelectedConversation(prev => 
        prev ? { ...prev, is_pinned_by_agent: !isPinned, pinned_at: !isPinned ? new Date().toISOString() : null } : null
      );
    }
    
    toast.success(isPinned ? "Désépinglée" : "Épinglée");
  };

  const handleArchive = async (convId: string) => {
    const { data, error } = await supabase.rpc('archive_conversation', { p_conversation_id: convId });
    if (error || !(data as any)?.success) { 
      toast.error("Échec de l'archivage"); 
      return; 
    }
    
    setConversations((prev) =>
      prev.map((c) =>
        c.id === convId
          ? { ...c, is_archived: true, archived_at: new Date().toISOString() }
          : c
      )
    );
    
    if (selectedConversation?.id === convId) {
      setSelectedConversation(prev => 
        prev ? { ...prev, is_archived: true, archived_at: new Date().toISOString() } : null
      );
    }
    
    toast.success("Conversation archivée");
  };

  const handleUnarchive = async (convId: string) => {
    const { data, error } = await supabase.rpc('unarchive_conversation', { p_conversation_id: convId });
    if (error || !(data as any)?.success) { 
      toast.error("Échec du désarchivage"); 
      return; 
    }
    
    setConversations((prev) =>
      prev.map((c) =>
        c.id === convId
          ? { ...c, is_archived: false, archived_at: null }
          : c
      )
    );
    
    if (selectedConversation?.id === convId) {
      setSelectedConversation(prev => 
        prev ? { ...prev, is_archived: false, archived_at: null } : null
      );
    }
    
    toast.success("Conversation désarchivée");
  };

  const filteredConversations = conversations
    .filter((conv) => {
      const matchesSearch = !searchQuery ||
        conv.user_profile?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.user_profile?.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.last_message_preview?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "archived" ? conv.is_archived : conv.status === statusFilter);
      const matchesService = serviceFilter === "all" || conv.service_id === serviceFilter;
      // Hide archived conversations from all tabs except "archived"
      const showArchived = statusFilter === "archived" || !conv.is_archived;
      
      // Assignment filter: mine = my services, unassigned = no agent or service without assigned agent
      let matchesAssignment = true;
      if (assignmentFilter === "mine") {
        matchesAssignment = conv.service_id !== null && agentServiceIds.includes(conv.service_id);
      } else if (assignmentFilter === "unassigned") {
        matchesAssignment = conv.assigned_agent_id === null;
      }
      
      return matchesSearch && matchesStatus && matchesService && showArchived && matchesAssignment;
    })
    .sort((a, b) => {
      if (a.is_pinned_by_agent && !b.is_pinned_by_agent) return -1;
      if (!a.is_pinned_by_agent && b.is_pinned_by_agent) return 1;
      const priorityDiff = getStatusPriority(a.status) - getStatusPriority(b.status);
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime();
    });

  const handleOpenNotes = async (conv: Conversation) => {
    setNotesConversation(conv);
    setNotesSheetOpen(true);
    const { data } = await supabase
      .from("internal_notes")
      .select("id, content, created_at, agent_id")
      .eq("conversation_id", conv.id)
      .order("created_at", { ascending: false });
    if (data) setNotes(data);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !user || !notesConversation || savingNote) return;
    setSavingNote(true);
    const { error } = await supabase.from("internal_notes").insert({
      conversation_id: notesConversation.id,
      agent_id: user.id,
      content: newNote.trim(),
    });
    if (error) { toast.error("Erreur"); } 
    else {
      toast.success("Note ajoutée");
      setNewNote("");
      const { data } = await supabase
        .from("internal_notes")
        .select("id, content, created_at, agent_id")
        .eq("conversation_id", notesConversation.id)
        .order("created_at", { ascending: false });
      if (data) setNotes(data);
    }
    setSavingNote(false);
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!user || deletingNoteId) return;
    
    if (!confirm("Supprimer cette note ?")) return;
    
    setDeletingNoteId(noteId);
    const { error } = await supabase
      .from("internal_notes")
      .delete()
      .eq("id", noteId)
      .eq("agent_id", user.id); // Only delete own notes
    
    if (error) {
      toast.error("Erreur lors de la suppression");
    } else {
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success("Note supprimée");
    }
    setDeletingNoteId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Mobile: Show chat full screen when conversation selected
  if (isMobile && selectedConversation) {
    return (
      <div className="min-h-screen flex flex-col">
        <AgentChatView
          conversation={selectedConversation}
          onStatusChange={(status) => {
            setSelectedConversation((prev) => prev ? { ...prev, status } : null);
            setConversations((prev) => prev.map((c) => c.id === selectedConversation.id ? { ...c, status } : c));
          }}
          onTakeOver={() => handleTakeOver(selectedConversation.id)}
          onBack={() => setSelectedConversation(null)}
          isMobile={true}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full">
      {/* Sidebar - Conversation List */}
      <aside className={`
        border-r border-border/50 flex flex-col h-screen bg-background/80 backdrop-blur-xl
        transition-all duration-300 ease-out
        ${isMobile ? "w-full" : sidebarCollapsed ? "w-16" : "w-80 lg:w-96"}
      `}>
        {/* Header */}
        <header 
          className={`border-b border-border/50 ${sidebarCollapsed ? "px-2 pb-2" : "px-4 pb-4"}`}
          style={{ paddingTop: 'max(2.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))' }}
        >
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full" onClick={() => setSidebarCollapsed(false)}>
                <PanelLeft size={20} />
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                    <span className="text-primary font-medium text-xs">
                      {profile?.first_name?.charAt(0) || "?"}{profile?.last_name?.charAt(0) || ""}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent side="right" align="start" className="w-44 p-1">
                  <Link to="/profile" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-md transition-colors">
                    <User size={14} />
                    Profil
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-md transition-colors">
                      <Settings size={14} />
                      Administration
                    </Link>
                  )}
                  <button 
                    onClick={signOut}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                  >
                    <LogOut size={14} />
                    Déconnexion
                  </button>
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {!isMobile && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => setSidebarCollapsed(true)}>
                      <PanelLeftClose size={18} />
                    </Button>
                  )}
                  <div>
                    <h1 className="font-serif text-lg font-semibold text-foreground">Inbox</h1>
                    <p className="text-[11px] text-muted-foreground">{profile?.first_name} {profile?.last_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  <Link to="/profile">
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" title="Profil"><User size={16} /></Button>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin">
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" title="Admin"><Settings size={16} /></Button>
                    </Link>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={signOut} title="Déconnexion">
                    <LogOut size={16} />
                  </Button>
                </div>
              </div>

              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm bg-muted/50 border-0" />
              </div>

              <div className="mb-3">
                <Select value={serviceFilter} onValueChange={setServiceFilter}>
                  <SelectTrigger className="w-full h-9 text-sm bg-muted/50 border-0">
                    <SelectValue placeholder="Filtrer par service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">Tous les services</div>
                    </SelectItem>
                    {serviceOptions.map((svc) => (
                      <SelectItem key={svc.id} value={svc.id}>
                        <div className="flex items-center gap-2">
                          {svc.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Assignment filter tabs */}
              {agentServiceIds.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1.5">
                  {[
                    { key: "all", label: "Tous", icon: Inbox },
                    { key: "mine", label: "Mes services", icon: UserCheck },
                    { key: "unassigned", label: "Non assignées", icon: Users },
                  ].map((filter) => (
                    <button
                      key={filter.key}
                      onClick={() => setAssignmentFilter(filter.key as "all" | "mine" | "unassigned")}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-all whitespace-nowrap flex items-center gap-1 ${
                        assignmentFilter === filter.key
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted/70 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <filter.icon size={12} />
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
                {[
                  { key: "all", label: "Tous" },
                  { key: "open", label: "Ouverts" },
                  { key: "pending", label: "En attente" },
                  { key: "archived", label: "Archivés" },
                ].map((status) => (
                  <button
                    key={status.key}
                    onClick={() => setStatusFilter(status.key)}
                    className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-all whitespace-nowrap ${
                      statusFilter === status.key
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/70 text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </header>

        {/* Conversation List */}
        <div className="flex-1 overflow-auto">
          {sidebarCollapsed ? (
            <div className="py-2 space-y-1">
              {filteredConversations.map((conv) => {
                const initials = `${conv.user_profile?.first_name?.charAt(0) || "?"}${conv.user_profile?.last_name?.charAt(0) || ""}`;
                const isSelected = selectedConversation?.id === conv.id;
                const hasUnread = conv.unread_count_agent > 0;
                
                return (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`w-full flex justify-center py-1.5 transition-all ${isSelected ? "bg-accent/60" : "hover:bg-accent/40"}`}
                  >
                    <div className="relative">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium ${isSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {initials}
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background ${
                        conv.status === "open" ? "bg-emerald-500" : conv.status === "pending" ? "bg-amber-500" : "bg-muted-foreground/50"
                      }`} />
                      {hasUnread && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
                          {conv.unread_count_agent > 9 ? "9+" : conv.unread_count_agent}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            filteredConversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageCircle size={32} className="mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">Aucune conversation</p>
              </div>
            ) : (
              <div className="divide-y divide-border/30">
                {filteredConversations.map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    isSelected={selectedConversation?.id === conv.id}
                    isAdmin={isAdmin}
                    currentUserId={user?.id}
                    onSelect={setSelectedConversation}
                    onTakeOver={(id) => handleTakeOver(id)}
                    onOpenNotes={handleOpenNotes}
                    onTogglePin={handleTogglePin}
                    onArchive={handleArchive}
                    onUnarchive={handleUnarchive}
                  />
                ))}
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="p-4 flex justify-center">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={loadMoreConversations}
                      disabled={loadingMore}
                      className="w-full"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={14} className="mr-2 animate-spin" />
                          Chargement...
                        </>
                      ) : (
                        "Voir plus"
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </aside>

      {/* Notes Sheet */}
      <Sheet open={notesSheetOpen} onOpenChange={setNotesSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="font-serif flex items-center gap-2"><StickyNote size={18} />Notes internes</SheetTitle>
          </SheetHeader>
          {notesConversation && (
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Textarea placeholder="Ajouter une note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={3} />
                <Button onClick={handleAddNote} disabled={!newNote.trim() || savingNote} size="sm" className="w-full">
                  {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                </Button>
              </div>
              <div className="space-y-3 max-h-[60vh] overflow-auto">
                {notes.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucune note</p>
                ) : (
                  notes.map((note) => (
                    <div key={note.id} className="p-3 bg-muted/50 rounded-lg group relative">
                      <p className="text-sm whitespace-pre-wrap pr-8">{note.content}</p>
                      <p className="text-[10px] text-muted-foreground mt-2">{new Date(note.created_at).toLocaleString("fr-FR")}</p>
                      {/* Delete button - only for own notes */}
                      {note.agent_id === user?.id && (
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={deletingNoteId === note.id}
                          className="absolute top-2 right-2 p-1 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                          title="Supprimer"
                        >
                          {deletingNoteId === note.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <span className="text-xs">✕</span>
                          )}
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Main Chat View - Desktop only */}
      {!isMobile && (
        <main className="flex-1 flex flex-col h-screen">
          {selectedConversation ? (
            <AgentChatView
              conversation={selectedConversation}
              onStatusChange={(status) => {
                setSelectedConversation((prev) => prev ? { ...prev, status } : null);
                setConversations((prev) => prev.map((c) => c.id === selectedConversation.id ? { ...c, status } : c));
              }}
              onTakeOver={() => handleTakeOver(selectedConversation.id)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Users size={48} className="mx-auto text-muted-foreground mb-4" />
                <h2 className="font-serif text-xl font-medium text-foreground mb-2">Sélectionnez une conversation</h2>
                <p className="text-sm text-muted-foreground">Choisissez une conversation pour commencer</p>
              </div>
            </div>
          )}
        </main>
      )}
    </div>
  );
}
